import React, { useState, useEffect } from "react";
import { Alert, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import {
  checkEditorPermission,
  checkViewerPermission,
  findSubModuleByName,
} from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { CreateWorkAdjustment } from "./CreateWorkAdjustment";
import { EditWorkAdjustment } from "./EditWorkAdjustment";
import { AttendanceRequestScreen } from "./attendance/AttendanceRequestScreen";
import { ATTENDANCE_TYPES } from "./attendance/attendanceHelpers";

// Provisioning grants viewing access independently of request eligibility.
const TAB_SUBMODULE_NAME = {
  permission: "Permission",
  onduty: "On Duty",
  compoff: "Comp Off",
  break: "Break",
};

export const WorkAdjustment = ({ initialTab }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  // Keep the requested screen stable when provisioning or eligibility changes.
  // Never substitute Permission for an explicitly selected Break link.
  const activeTab = Object.prototype.hasOwnProperty.call(
    TAB_SUBMODULE_NAME,
    initialTab,
  )
    ? initialTab
    : "permission";
  const canView = checkViewerPermission(
    userScreenAccess?.module_details,
    "My Request",
    TAB_SUBMODULE_NAME[activeTab],
  );
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [updateData, setUpdateData] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [defaultType, setDefaultType] = useState("");
  // "break" swaps the generic Work Adjustment drawer for the dedicated
  // Manual Break / Cancel Break request modal.
  const [createMode, setCreateMode] = useState("general");

  const {
    result: specialAttendanceData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    canView && domain && userDetails?.id
      ? `${domain}my_zone/apply/work_adjustment/pre-special-attendance/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`
      : null,
  );

  const check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    TAB_SUBMODULE_NAME[activeTab],
  );

  // Resolve saved-view preferences using the selected request submodule.
  const tabSubModuleId = findSubModuleByName(
    userScreenAccess?.module_details,
    "My Request",
    TAB_SUBMODULE_NAME[activeTab],
  )?.sub_module_id;

  useEffect(() => {
    setIsCreatePanelOpen(false);
    setIsEditPanelOpen(false);
    setSelectedData(null);
  }, [activeTab, canView, check_access]);

  const openCreate = (presetType = "", mode = "general") => {
    if (check_access) {
      setDefaultType(presetType);
      setCreateMode(mode);
      setIsCreatePanelOpen(true);
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to create work adjustment",
      );
    }
  };

  const openView = (row) => {
    setSelectedData(row);
    setUpdateData(true);
    setIsEditPanelOpen(true);
  };

  const data = Array.isArray(specialAttendanceData) ? specialAttendanceData : [];

  if (!canView) {
    return (
      <Alert severity="info">
        You do not have access to view {TAB_SUBMODULE_NAME[activeTab]} requests.
      </Alert>
    );
  }

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ width: "100%" }}>
        <AttendanceRequestScreen
          key={activeTab}
          typeKey={activeTab}
          data={data}
          checkAccess={check_access}
          subModuleId={tabSubModuleId}
          onNew={() =>
            openCreate(
              ATTENDANCE_TYPES[activeTab]?.defaultType || "",
              activeTab === "break" ? "break" : "general",
            )
          }
          onRowClick={openView}
        />
      </Box>

      {isCreatePanelOpen ? (
        <CreateWorkAdjustment
          open={isCreatePanelOpen}
          onClose={() => setIsCreatePanelOpen(false)}
          reCallApi={reCallApi}
          defaultType={defaultType}
          mode={createMode}
        />
      ) : null}
      {isEditPanelOpen ? (
        <EditWorkAdjustment
          open={isEditPanelOpen}
          onClose={() => setIsEditPanelOpen(false)}
          setUpdateData={setUpdateData}
          updateData={updateData}
          selectedData={selectedData}
          reCallApi={reCallApi}
          check_access={check_access}
        />
      ) : null}
    </React.Fragment>
  );
};
